import './AuditRatesList.less';

import { Button, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useState } from 'react';

import {
  useRateAuditByCarrier,
  useRateAuditSummary,
} from '../../hooks/apiHooks';
import {
  AuditCarrierSummary,
  AuditDuplicateGroup,
  Rate,
} from '../../models/Rate';
import Container from '../common/Container';
import PanelHeader from '../common/PanelHeader';
import MergeRatesModal from '../modals/MergeRatesModal';

const { Text } = Typography;

// main component
const AuditRatesList: React.FC = () => {
  const { auditSummary, error, isLoading } = useRateAuditSummary();
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const columns: ColumnsType<AuditCarrierSummary> = [
    {
      dataIndex: 'carrier_name',
      key: 'carrier_name',
      title: 'Carrier Name',
    },
  ];

  const expandedRowRender = (record: AuditCarrierSummary) => {
    return <CarrierDuplicateGroups carrierId={record.carrier_id} />;
  };

  if (error) {
    return (
      <Container>
        <PanelHeader />
        <div style={{ color: 'red', padding: '20px' }}>
          Error loading audit data: {error.message}
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <PanelHeader />

      {isLoading && auditSummary.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      ) : auditSummary.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Text type="secondary">No carriers with duplicate rates found.</Text>
        </div>
      ) : (
        <>
          <Text style={{ display: 'block', marginBottom: 12 }} type="secondary">
            Found {auditSummary.length} carrier(s) with duplicate rates. Expand
            a row to see details.
          </Text>
          <Table
            columns={columns}
            dataSource={auditSummary}
            expandable={{
              expandedRowKeys,
              expandedRowRender,
              onExpandedRowsChange: (keys) =>
                setExpandedRowKeys(keys as React.Key[]),
            }}
            loading={isLoading}
            pagination={{
              defaultPageSize: 10,
              pageSizeOptions: ['10', '20', '50'],
              showSizeChanger: true,
              showTotal: (total, range) => `Showing ${total} carriers`,
            }}
            rowClassName="rate-row-highlight"
            rowKey={(record) => record.carrier_id}
            onRow={(record) => ({
              onClick: () => {
                const isExpanded = expandedRowKeys.includes(record.carrier_id);
                if (isExpanded) {
                  setExpandedRowKeys(
                    expandedRowKeys.filter((key) => key !== record.carrier_id)
                  );
                } else {
                  setExpandedRowKeys([...expandedRowKeys, record.carrier_id]);
                }
              },
              style: {
                cursor: 'pointer',
              },
            })}
          />
        </>
      )}
    </Container>
  );
};

// expanded row component
const CarrierDuplicateGroups: React.FC<{ carrierId: number }> = ({
  carrierId,
}) => {
  const { auditDetail, error, isLoading } = useRateAuditByCarrier(carrierId);

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: 'red', padding: '20px' }}>
        Error loading duplicate rates: {error.message}
      </div>
    );
  }

  if (!auditDetail || auditDetail.duplicateGroups.length === 0) {
    return (
      <div style={{ padding: '20px' }}>
        <Text type="secondary">No duplicate rates found.</Text>
      </div>
    );
  }

  return (
    <div>
      {auditDetail.duplicateGroups.map((group, groupIndex) => (
        <DuplicateGroupTable
          key={`group-${groupIndex}`}
          carrierId={carrierId}
          group={group}
          groupIndex={groupIndex}
        />
      ))}
    </div>
  );
};

const DuplicateGroupTable: React.FC<{
  carrierId: number;
  group: AuditDuplicateGroup;
  groupIndex: number;
}> = ({ carrierId, group, groupIndex }) => {
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const { refetch } = useRateAuditByCarrier(carrierId);

  const handleMergeSuccess = () => {
    refetch();
  };
  const classIds = new Set<string>();
  group.rates.forEach((rate) => {
    Object.keys(rate.classRate || {}).forEach((classId) => {
      classIds.add(classId);
    });
  });

  // finding class name from any rate that has it
  const classColumns: ColumnsType<Rate> = Array.from(classIds).map(
    (classId) => {
      const classIdNum = parseInt(classId);
      let className = classId;
      for (const rate of group.rates) {
        const name = rate.classRate?.[classIdNum]?.name;
        if (name) {
          className = name;
          break;
        }
      }
      return {
        ellipsis: true,
        key: `class_${classId}`,
        render: (_, rate) => {
          const value = rate.classRate?.[classIdNum]?.value;
          return value != null ? `$${value.toFixed(2)}` : '-';
        },
        title: className,
      };
    }
  );

  const rateColumns: ColumnsType<Rate> = [
    {
      dataIndex: 'id',
      key: 'id',
      title: 'Rate ID',
      width: 100,
    },
    ...classColumns,
    {
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value) => (value ? new Date(value).toLocaleDateString() : '-'),
      title: 'Updated At',
      width: 100,
    },
  ];

  return (
    <div
      style={{
        border: '1px solid #d9d9d9',
        borderRadius: '4px',
        marginBottom: '24px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          borderBottom: '1px solid #d9d9d9',
          padding: '12px 0px',
        }}
      >
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            marginLeft: '9px',
          }}
        >
          <Text strong>{group.origin}</Text>
          {group.rates[0]?.originShipTypeName && (
            <Tag color="blue">{group.rates[0].originShipTypeName}</Tag>
          )}
          <Text
            style={{ fontSize: '16px', marginRight: '8px' }}
            type="secondary"
          >
            →
          </Text>
          <Text strong>{group.destination}</Text>
          {group.rates[0]?.destinationShipTypeName && (
            <Tag color="blue">{group.rates[0].destinationShipTypeName}</Tag>
          )}
          {group.rates[0]?.modeName && (
            <Tag color="green">{group.rates[0].modeName}</Tag>
          )}
          <Button
            size="small"
            style={{ marginLeft: 'auto' }}
            type="primary"
            onClick={() => setIsMergeModalOpen(true)}
          >
            Merge
          </Button>
          <Tag
            color={group.duplicateCount > 2 ? 'red' : 'orange'}
            style={{ marginLeft: '8px' }}
          >
            {group.duplicateCount} duplicates
          </Tag>
        </div>
      </div>
      <Table
        bordered={true}
        className="audit-rates-table"
        columns={rateColumns}
        dataSource={group.rates}
        pagination={false}
        rowClassName="rate-row-highlight"
        rowKey={(rate) => rate.id}
        scroll={{ x: 'max-content' }}
        size="small"
      />
      {isMergeModalOpen && (
        <MergeRatesModal
          carrierId={carrierId}
          group={group}
          onClose={() => setIsMergeModalOpen(false)}
          onSuccess={handleMergeSuccess}
        />
      )}
    </div>
  );
};

export default AuditRatesList;
